/**
 * WCAG 2.1 A fix: variant swatches (color, size, etc.) are not operable
 * from the keyboard, and are not announced as a named group.
 *
 * The theme's swatch picker (snippets/product-form.liquid) renders each
 * variant option (e.g. Bracelet Size S/M/L/XL) as a plain <div
 * data-swatch-item> with a click handler bound elsewhere in the bundled
 * theme JS. A <div> is never in the tab order and has no default
 * keyboard-activation behavior, so keyboard and screen reader users could
 * not reach or select these options at all.
 *
 * The template renders each item with role="radio", tabindex="0" and a
 * static aria-checked (accessible name left as the item's own visible
 * text, e.g. "S"), inside a wrapper with role="radiogroup" and an
 * aria-label naming the option (e.g. "Bracelet Size"). This follows the
 * standard ARIA radio-group pattern: a screen reader announces the
 * group's name once on entering the group, then just each option's own
 * value as you move between them.
 *
 * At least some products rebuild this entire swatch block client-side
 * after load (confirmed: regenerated items carry inline styles the
 * Liquid template never sets), discarding the template's attributes on
 * items and on the wrapper around them - including, it turns out, even
 * the [data-swatch-option] container this script originally relied on to
 * locate the option's visible heading, so a first pass at this fix
 * managed to restore role="radiogroup" on the regenerated wrapper but
 * came up with no label to put on it. This version looks for the
 * .t4s-swatch__title heading inside the swatch list's own parent
 * container instead of requiring that specific attribute, which is more
 * likely to survive whatever markup the page regenerates.
 *
 * 1. Activate on Enter/Space. tabindex alone makes an element focusable,
 *    it does not make it "clickable" the way a real <button> is native to
 *    the keyboard. This listens for Enter/Space on any [data-swatch-item]
 *    and dispatches a real click event, so the theme's existing
 *    (unmodified) click handler fires exactly as it would for a mouse
 *    click - no bundled/minified library code needs to change.
 *
 * 2. Keep aria-checked in sync after selection. Choosing a swatch is a
 *    client-side operation (the theme toggles an "is--selected" class);
 *    the initial aria-checked value from the server-rendered page would
 *    otherwise go stale the moment the shopper picks a different option.
 *
 * 3. Defensively (re)apply role/tabindex/aria-checked to any
 *    [data-swatch-item], and role/aria-label to its immediate parent (the
 *    swatch list container), whenever either is missing. The group's
 *    label is read from a .t4s-swatch__title heading found either as a
 *    previous sibling of the swatch list, or anywhere inside the swatch
 *    list's parent element, with the live "current value" span stripped
 *    out (e.g. "Bracelet Size:" -> "Bracelet Size").
 *
 * A MutationObserver watches for class changes (to keep aria-checked
 * current) and for newly added nodes (to apply the missing attributes to
 * anything that arrives without them), plus a periodic re-scan as a
 * safety net for regeneration patterns a MutationObserver might miss -
 * the same defensive pattern used in wcag-focus-fix.js for this theme's
 * other accessibility fixes.
 */
(function () {
  "use strict";

  var ITEM_SELECTOR = "[data-swatch-item]";
  var TITLE_SELECTOR = ".t4s-swatch__title";
  var CURRENT_VALUE_SELECTOR = "[data-current-value]";
  var SELECTED_CLASS = "is--selected";

  function isSelected(el) {
    return el.classList.contains(SELECTED_CLASS);
  }

  function cleanHeadingText(titleEl) {
    var clone = titleEl.cloneNode(true);
    var currentValueEl = clone.querySelector(CURRENT_VALUE_SELECTOR);
    if (currentValueEl && currentValueEl.parentNode) {
      currentValueEl.parentNode.removeChild(currentValueEl);
    }
    var text = (clone.textContent || "").replace(/\s+/g, " ").trim();
    return text.replace(/:$/, "");
  }

  function headingLabelFor(group) {
    if (!group) return "";

    var prev = group.previousElementSibling;
    if (prev && prev.matches && prev.matches(TITLE_SELECTOR)) {
      return cleanHeadingText(prev);
    }

    var container = group.parentElement;
    if (container) {
      var titleEl = container.querySelector(TITLE_SELECTOR);
      if (titleEl) {
        return cleanHeadingText(titleEl);
      }
    }

    return "";
  }

  function ensureGroupAttrs(item) {
    var group = item.parentElement;
    if (!group) return;
    if (!group.hasAttribute("role")) {
      group.setAttribute("role", "radiogroup");
    }
    if (!group.getAttribute("aria-label")) {
      var label = headingLabelFor(group);
      if (label) {
        group.setAttribute("aria-label", label);
      }
    }
  }

  function ensureItemAttrs(el) {
    if (!el.hasAttribute("role")) {
      el.setAttribute("role", "radio");
    }
    if (!el.hasAttribute("tabindex")) {
      el.setAttribute("tabindex", "0");
    }
    var checked = isSelected(el) ? "true" : "false";
    if (el.getAttribute("aria-checked") !== checked) {
      el.setAttribute("aria-checked", checked);
    }
    ensureGroupAttrs(el);
  }

  function handleKeydown(event) {
    var key = event.key;
    if (key !== "Enter" && key !== " " && key !== "Spacebar") return;

    var target = event.target.closest
      ? event.target.closest(ITEM_SELECTOR)
      : null;
    if (!target) return;

    // Space would otherwise scroll the page, same as it would on a
    // native button/radio.
    event.preventDefault();
    target.click();
  }

  function scanAll(root) {
    root = root || document;
    var items = root.querySelectorAll(ITEM_SELECTOR);
    for (var i = 0; i < items.length; i++) {
      ensureItemAttrs(items[i]);
    }
  }

  function start() {
    document.addEventListener("keydown", handleKeydown, true);

    scanAll();

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          var target = mutation.target;
          if (target.matches && target.matches(ITEM_SELECTOR)) {
            ensureItemAttrs(target);
          }
        } else if (mutation.type === "childList") {
          for (var j = 0; j < mutation.addedNodes.length; j++) {
            var added = mutation.addedNodes[j];
            if (added.nodeType !== 1) continue;
            if (added.matches && added.matches(ITEM_SELECTOR)) {
              ensureItemAttrs(added);
            }
            if (added.querySelectorAll) {
              var nested = added.querySelectorAll(ITEM_SELECTOR);
              for (var k = 0; k < nested.length; k++) {
                ensureItemAttrs(nested[k]);
              }
            }
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
      childList: true,
      subtree: true,
    });

    // Safety net: some swatch re-renders may replace nodes without
    // triggering a clean childList mutation we catch above (e.g. multiple
    // rapid innerHTML writes). A light periodic re-scan self-corrects
    // within well under the time it takes a keyboard user to tab to it.
    setInterval(scanAll, 500);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
